import { bound } from "@frui.ts/helpers";
import { ClassType } from "class-transformer/ClassTransformer";
import { action, observable } from "mobx";
import { EntityNames, InternalEnumsMap, SystemEnums } from "config";
import DistributorRepository from "repositories/distributorRepository";
import EnumRepository from "repositories/enumRepository";
import PermissionAreaRepository from "repositories/permissionAreaRepository";
import type { ISelectItem } from "types";
import { getEntityName, getEntityType } from "utils/entityName";
import { getFormatOptions } from "utils/optionable";
import enumValues from "utils/enumValues";
import type OptionsViewModel from "viewModels/optionsViewModel";
import { IParams } from "viewModels/optionsViewModel";
import type { SimpleListEnumTypes, SystemEnumTypes } from "./enumService";
import type EnumService from "./enumService";
import snakeCase from "lodash/snakeCase";
import camelCase from "lodash/camelCase";
import pluralize from "pluralize";
import type EventBus from "./eventBus";
import type { InternalEnumNames, InternalEnums } from "./localizationService";
import type LocalizationService from "./localizationService";

export interface EntityLoader {
  repository: any;
  loaderMethod?: string;
  loaderParamNames?: string[];
}

export default class OptionsService {
  private unsubscribtions: Array<() => void> = [];
  @observable private optionsViewModels: Map<EntityNames, OptionsViewModel<any>> = new Map();
  private entityLoaders = new Map<EntityNames, EntityLoader>([
    ["permissionArea", { repository: PermissionAreaRepository }],
    ["distributor", { repository: DistributorRepository }],
    ["accountingTemplateResponse", { repository: EnumRepository, loaderMethod: "getAllAccountingTemplates" }],
    ["region", { repository: EnumRepository, loaderMethod: "getAllRegions" }],
    ["municipality", { repository: EnumRepository, loaderMethod: "getAllMunicipality" }],
    ["gatewayExternalIdentType", { repository: EnumRepository, loaderMethod: "getAllGateways" }],
    ["kiosk", { repository: EnumRepository, loaderMethod: "getAllKiosks" }],
    ["road", { repository: EnumRepository, loaderMethod: "getAllRoads" }],
    ["bankAccountEnum", { repository: EnumRepository, loaderMethod: "getAllBankAccounts" }],
    ["systemComponent", { repository: EnumRepository, loaderMethod: "getAllSystemComponents" }],
    ["externalIdentityProvider", { repository: EnumRepository, loaderMethod: "getAllExternalIdentityProviders" }],
    [
      "transferRefundForRefundOfRefundPaymentTemplate",
      { repository: EnumRepository, loaderMethod: "getTransferRefundForRefundOfRefundPaymentTemplates" },
    ],
    ["transferRefundPaymentTemplate", { repository: EnumRepository, loaderMethod: "getTransferRefundPaymentTemplates" }],
    ["payGateRefundPaymentTemplate", { repository: EnumRepository, loaderMethod: "getPayGateRefundPaymentTemplates" }],
    ["notifiableExemptionReasons", { repository: EnumRepository, loaderMethod: "getAllNotifiableExemptionReasons" }],
    [
      "payGateToTransferRefundPaymentTemplate",
      { repository: EnumRepository, loaderMethod: "getPayGateToTransferRefundPaymentTemplates" },
    ],
    [
      "distributorPaymentRequest",
      {
        repository: DistributorRepository,
        loaderMethod: "distributorPaymentRequestsSimpleList",
        loaderParamNames: ["distributorId"],
      },
    ],
  ]);

  constructor(
    private optionsViewModelFactory: ReturnType<typeof OptionsViewModel.Factory>,
    private eventBus: EventBus,
    private enumService: EnumService,
    private localizationService: LocalizationService
  ) {}

  optionsViewModel<T>(entity: ClassType<T>): OptionsViewModel<T> {
    const formatOptions = getFormatOptions(entity) ?? (item => ({ id: item.id, label: item.name }));
    const entityName = getEntityName(entity);

    if (!entityName) {
      throw new Error("Cannot find name of entity");
    }

    const entityLoader = this.entityLoaders.get(entityName);

    if (!entityLoader) {
      throw new Error(`Cannot find entity loader for ${entityName}`);
    }

    const repository = entityLoader.repository;
    const loaderMethod = entityLoader.loaderMethod;
    const loaderParamNames = entityLoader.loaderParamNames;

    let optionsViewModel = this.optionsViewModels.get(entityName);
    if (!optionsViewModel) {
      optionsViewModel = this.optionsViewModelFactory(repository, formatOptions, loaderMethod, loaderParamNames);
      this.optionsViewModels.set(entityName, optionsViewModel);
    }

    this.unsubscribtions.push(this.eventBus.subscribe(`${entityName}.*`, () => this.refreshEntities(entityName)));
    return optionsViewModel;
  }

  entityOptions<T>(entity: ClassType<T>): ISelectItem[] {
    const entityName = getEntityName(entity);
    const enumName = pluralize.plural(snakeCase(entityName)) as any;

    if (SystemEnums.includes(enumName)) {
      return this.enumOptions(enumName);
    } else {
      return this.optionsViewModel(entity).options;
    }
  }

  @action.bound
  setParams<T>(entity: ClassType<T>, params?: IParams) {
    this.optionsViewModel(entity).setLoaderParams(params);
  }

  internalEnumOptions(internalEnumType: InternalEnums, filterFunction?: (item: any) => boolean): ISelectItem[] {
    let values = enumValues(internalEnumType);
    const internalEnumName = (Object.keys(InternalEnumsMap) as InternalEnumNames[]).find(
      (key: InternalEnumNames) => InternalEnumsMap[key] === internalEnumType
    );

    if (filterFunction) {
      values = values.filter(filterFunction);
    }

    return values.map(item => ({
      id: item,
      label: this.localizationService.translateEnum(internalEnumName as any, item as any),
    }));
  }

  enumOptions(enumName: SystemEnumTypes | SimpleListEnumTypes, customFormatFunction?: (item: any) => ISelectItem) {
    const entityName = pluralize.singular(camelCase(enumName)) as EntityNames;
    const entity = getEntityType(entityName);
    let formatOptions = (item: any) => ({ id: item.id, label: item.name });

    if (customFormatFunction) {
      formatOptions = customFormatFunction;
    } else if (entity) {
      const entityFormatFunction = getFormatOptions(entity);
      if (entityFormatFunction) {
        formatOptions = entityFormatFunction;
      }
    }

    return this.enumService.values(enumName as any).map(formatOptions);
  }

  @bound
  private refreshEntities(entityName: EntityNames) {
    this.optionsViewModels.get(entityName)?.invalidate();
  }
}
